import { useState } from "react";
import { DAY_MS, PLAN_HOUR, TIME_WORDS, atLocal, clockText, dateAfter, dayBar, inWindow, localOf, nextChange, rangeText, weekdayOf, windowText } from "@speisekarte/core";
import type { MenuSection, PlanWhat, Planned, SectionTimes, Weekday, Window } from "@speisekarte/core";
import { api, messageOf, store } from "../store.ts";
import type { Data } from "../store.ts";
import { plannedPdfOld, renewPlannedPdf } from "../outside.ts";
import { uhr, whenText } from "../timing.ts";

const DAYS = TIME_WORDS.de.days;

/** „11:30“ → 690; anything else null. */
export function minutesOf(v: string): number | null {
  const m = /^(\d{1,2}):(\d{2})$/.exec(v.trim());
  if (!m) return null;
  const h = Number(m[1]);
  const min = Number(m[2]);
  return h <= 23 && min <= 59 ? h * 60 + min : null;
}

/** How a temporary section is on paper (F2), in words. */
const PRINTS: { id: NonNullable<SectionTimes["print"]> | "time"; label: string }[] = [
  { id: "time", label: "mit Zeit" },
  { id: "plain", label: "ohne Zeit" },
  { id: "hide", label: "nicht drucken" },
];

/** What a temporary section means for the guests, in one sentence (§ 6.1, rule 3). */
export function timesSentence(title: string, t: SectionTimes): string {
  const when = windowText(t);
  const paper = t.print === "hide" ? "Gedruckt fehlt er." : t.print === "plain" ? "Gedruckt steht er da, ohne Zeit." : `Gedruckt steht er da, mit „${when}“ unter der Überschrift.`;
  return `Gäste sehen ${title ? `„${title}“` : "den Abschnitt"} ${when}. Sonst steht oben: „${title ? `${title} ` : ""}${when}“. ${paper}`;
}

/**
 * Zeiten im Netz (S62, Stufe 1; Bild 1): when a section is on the QR-Code-Speisekarte – weekdays,
 * from, to – and how it is on paper. Beneath, at once, what that means for the guests.
 */
export function TimesSheet({ section, onSave, onClose }: { section: Pick<MenuSection, "title" | "web">; onSave: (web: SectionTimes | undefined) => void; onClose: () => void }) {
  const lunch = /mittag/i.test(section.title);
  const [days, setDays] = useState<Weekday[]>(section.web?.days ?? (lunch ? [0, 1, 2, 3, 4] : []));
  const [from, setFrom] = useState(clockText(section.web?.from ?? 690));
  const [to, setTo] = useState(clockText(section.web?.to === 1440 ? 0 : (section.web?.to ?? 870)));
  const [print, setPrint] = useState<SectionTimes["print"] | "time">(section.web?.print ?? "time");
  const f = minutesOf(from);
  const t = minutesOf(to);
  const valid = days.length > 0 && f !== null && t !== null && f !== t;
  const times: SectionTimes | null = valid ? { days: [...days].sort((a, b) => a - b), from: f!, to: t!, ...(print !== "time" ? { print: print as "plain" | "hide" } : {}) } : null;
  const toggle = (d: Weekday) => setDays((x) => (x.includes(d) ? x.filter((y) => y !== d) : [...x, d]));
  return (
    <div className="overlay" role="dialog" aria-label="Zeiten im Netz" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="dialog times-sheet" data-testid="times-sheet">
        <h2>Zeiten im Netz</h2>
        <p className="hint" style={{ marginTop: 0 }}>
          Wann {section.title ? `„${section.title}“` : "der Abschnitt"} auf der QR-Code-Speisekarte steht.
        </p>
        <div className="day-chips" role="group" aria-label="Wochentage">
          {DAYS.map((d, i) => (
            <button key={d} type="button" className={days.includes(i as Weekday) ? "on" : ""} aria-pressed={days.includes(i as Weekday)} onClick={() => toggle(i as Weekday)} data-testid={`times-day-${i}`}>
              {d}
            </button>
          ))}
        </div>
        <div className="times-row">
          <label>
            von
            <input type="time" value={from} onChange={(e) => setFrom(e.target.value)} data-testid="times-from" />
          </label>
          <label>
            bis
            <input type="time" value={to} onChange={(e) => setTo(e.target.value)} data-testid="times-to" />
          </label>
        </div>
        <p className="lab">gedruckt</p>
        <div className="day-chips print" role="radiogroup" aria-label="gedruckt">
          {PRINTS.map((p) => (
            <button key={p.id} type="button" role="radio" aria-checked={print === p.id} className={print === p.id ? "on" : ""} onClick={() => setPrint(p.id)} data-testid={`times-print-${p.id}`}>
              {p.label}
            </button>
          ))}
        </div>
        {times ? (
          <p className="ok-box" data-testid="times-say">
            {timesSentence(section.title, times)}
          </p>
        ) : (
          <p className="hint">{days.length === 0 ? "Bitte mindestens einen Tag wählen." : "Bitte zwei verschiedene Uhrzeiten."}</p>
        )}
        <div className="buttons">
          <button className="primary" disabled={!times} onClick={() => times && onSave(times)} data-testid="times-save">
            Übernehmen
          </button>
          {section.web && (
            <button onClick={() => onSave(undefined)} data-testid="times-always">
              Immer zeigen
            </button>
          )}
          <button onClick={onClose}>Abbrechen</button>
        </div>
      </div>
    </div>
  );
}

const named = (list: Pick<MenuSection, "title">[]) => list.map((s) => `„${s.title || "Abschnitt"}“`).join(", ");

/**
 * Jetzt (S62 § 6.1, Bild 3): what the guests see now and what comes next, in words, and the bar of
 * the day with the temporary sections of the menu on the net. Nothing without one.
 */
export function NowLine({ menu, at = Date.now() }: { menu: { title: string; sections: MenuSection[] }; at?: number }) {
  const temp = menu.sections.filter((s) => s.web && s.items.length);
  if (!temp.length) return null;
  const shown = temp.filter((s) => inWindow(s.web!, at));
  const hidden = temp.filter((s) => !inWindow(s.web!, at));
  const next = nextChange(temp.map((s) => s.web!), at);
  const flips = next ? temp.filter((s) => inWindow(s.web!, next) !== inWindow(s.web!, at)) : [];
  const on = flips.filter((s) => inWindow(s.web!, next!));
  const off = flips.filter((s) => !inWindow(s.web!, next!));
  const today = localOf(at);
  return (
    <div className="now-line" data-testid="now-line">
      <p style={{ margin: 0 }}>
        <b>Jetzt:</b> {menu.title}
        {shown.length > 0 && `, mit ${named(shown)}`}
        {hidden.length > 0 && `, ohne ${named(hidden)}`}.
      </p>
      {next && (
        <p className="muted" style={{ margin: "0.1rem 0 0" }} data-testid="now-next">
          Als Nächstes: {whenText(next, at)} {[on.length ? `mit ${named(on)}` : "", off.length ? `ohne ${named(off)}` : ""].filter(Boolean).join(", ")}.
        </p>
      )}
      <DayBar label={`Heute, ${DAYS_LONG[today.weekday]}`} date={today.date} minute={today.minute} parts={temp.map((s) => ({ name: s.title || "Abschnitt", web: s.web! }))} />
    </div>
  );
}

const DAYS_LONG = ["Montag", "Dienstag", "Mittwoch", "Donnerstag", "Freitag", "Samstag", "Sonntag"];

/** The bar of a day (§ 6.1, 2): 0 to 24 o'clock, a piece for each part in its time, a stroke for now. */
export function DayBar({ label, date, minute, parts }: { label: string; date: string; minute?: number; parts: { name: string; web: Window; tone?: string }[] }) {
  return (
    <div className="day-bar" data-testid="day-bar">
      <div className="day-bar-label">{label}</div>
      <div className="day-bar-track">
        {parts.flatMap((p, k) =>
          dayBar(p.web, date).map((x, j) => (
            <span key={`${k}-${j}`} className={`day-bar-seg ${p.tone ?? "tone-0"}`} style={{ left: `${(x.from / 1440) * 100}%`, width: `${((x.to - x.from) / 1440) * 100}%` }} title={`${p.name} ${rangeText(x.from, x.to)}`}>
              {p.name}
            </span>
          )),
        )}
        {minute !== undefined && <span className="day-bar-now" style={{ left: `${(minute / 1440) * 100}%` }} aria-hidden="true" />}
      </div>
      <div className="day-bar-ticks" aria-hidden="true">
        <span>0</span>
        <span>6</span>
        <span>12</span>
        <span>18</span>
        <span>24</span>
      </div>
    </div>
  );
}

/** When a change goes on the net (S62, Bild 5): now, tomorrow at the business's hour (F4), or at a moment. */
export type When = { kind: "now" } | { kind: "tomorrow" } | { kind: "at"; date: string; time: string };

/** The moment of a choice; null: now. */
export function whenAt(when: When, what: PlanWhat, now = Date.now()): number | null {
  if (when.kind === "now") return null;
  if (when.kind === "tomorrow") return atLocal(dateAfter(localOf(now).date, 1), PLAN_HOUR[what]);
  const m = minutesOf(when.time);
  return /^\d{4}-\d{2}-\d{2}$/.test(when.date) && m !== null ? atLocal(when.date, m) : NaN;
}

/** The choice to start with: after 18 o'clock a recommendation is for tomorrow (Bild 5), all else now. */
export const firstWhen = (what: PlanWhat, now = Date.now()): When => (what === "recommendations" && localOf(now).minute >= 18 * 60 ? { kind: "tomorrow" } : { kind: "now" });

/** Jetzt · Morgen ab 6:00 · Anderer Zeitpunkt … (Bild 5). */
export function WhenChoice({ what, value, onChange, testid }: { what: PlanWhat; value: When; onChange: (w: When) => void; testid: string }) {
  const now = Date.now();
  const tomorrow = dateAfter(localOf(now).date, 1);
  const other = value.kind === "at" ? value : { kind: "at" as const, date: tomorrow, time: clockText(PLAN_HOUR[what]) };
  return (
    <div className="when-choice" role="radiogroup" aria-label="Wann" data-testid={testid}>
      <label className="check">
        <input type="radio" checked={value.kind === "now"} onChange={() => onChange({ kind: "now" })} data-testid={`${testid}-now`} />
        Jetzt
      </label>
      <label className="check">
        <input type="radio" checked={value.kind === "tomorrow"} onChange={() => onChange({ kind: "tomorrow" })} data-testid={`${testid}-tomorrow`} />
        Morgen, {DAYS_LONG[weekdayOf(tomorrow)]}, ab {uhr(PLAN_HOUR[what])}
      </label>
      <label className="check">
        <input type="radio" checked={value.kind === "at"} onChange={() => onChange(other)} data-testid={`${testid}-at`} />
        Anderer Zeitpunkt …
      </label>
      {value.kind === "at" && (
        <div className="times-row">
          <label>
            Tag
            <input type="date" value={value.date} min={localOf(now).date} onChange={(e) => onChange({ ...value, date: e.target.value })} data-testid={`${testid}-date`} />
          </label>
          <label>
            ab
            <input type="time" value={value.time} onChange={(e) => onChange({ ...value, time: e.target.value })} data-testid={`${testid}-time`} />
          </label>
        </div>
      )}
    </div>
  );
}

/** A planned change as a line: „Mi 4.11. ab 6:00 · Empfehlung Mittwoch“. */
export const plannedLine = (p: Planned, data: Data) => `${whenText(p.at, Date.now())} · ${data.menus.find((m) => m.id === p.menu)?.title ?? "eine gelöschte Speisekarte"}`;

/**
 * Geplant (Bild 4, Bild 14): what goes on the net by itself, the soonest first, each with „Ändern“
 * (another moment) and „Nicht einplanen“; a planned PDF that its menu has left behind says so (Z-0).
 */
export function PlannedList({ data, what, slot, menu, may }: { data: Data; what: PlanWhat; slot?: string; menu?: string; may: boolean }) {
  const list = (data.settings.planned ?? []).filter((p) => p.what === what && (slot === undefined || p.slot === slot) && (menu === undefined || p.menu === menu)).sort((a, b) => a.at - b.at);
  const [moving, setMoving] = useState<{ id: string; when: When } | null>(null);
  const [state, setState] = useState<{ busy?: boolean; error?: string }>({});
  if (!list.length) return null;
  async function run(fn: () => Promise<unknown>) {
    setState({ busy: true });
    try {
      await store.change(fn);
      setState({});
      setMoving(null);
    } catch (err) {
      setState({ error: messageOf(err) });
    }
  }
  return (
    <div className="planned" data-testid={`planned-${what}`}>
      <p className="lab">Geplant</p>
      <ul className="planned-list">
        {list.map((p) => {
          const old = p.what === "pdf" && plannedPdfOld(p, data);
          return (
            <li key={p.id} data-testid="planned-row">
              <span className="planned-what">
                <b>{whenText(p.at, Date.now())}</b> · {data.menus.find((m) => m.id === p.menu)?.title ?? "eine gelöschte Speisekarte"}
                {p.what === "pdf" && <span className="muted small"> (PDF vom {new Date(p.madeAt).toLocaleDateString("de-DE")})</span>}
              </span>
              {may && (
                <span className="planned-actions">
                  <button className="link" onClick={() => setMoving(moving?.id === p.id ? null : { id: p.id, when: { kind: "at", date: localOf(p.at).date, time: clockText(localOf(p.at).minute) } })} data-testid="planned-move">
                    Ändern
                  </button>
                  <button className="link danger" onClick={() => void run(() => api(`/api/plan/${p.id}`, { method: "DELETE" }))} disabled={state.busy} data-testid="planned-remove">
                    Nicht einplanen
                  </button>
                </span>
              )}
              {old && (
                <p className="warn-box" data-testid="planned-old">
                  „{data.menus.find((m) => m.id === p.menu)?.title}“ hat sich seit dem Einplanen geändert; unter dem Link läge der alte Stand.
                  {may && (
                    <button className="primary" style={{ marginLeft: "0.5rem" }} disabled={state.busy} onClick={() => void run(() => renewPlannedPdf(p, data))} data-testid="planned-renew">
                      PDF neu erzeugen
                    </button>
                  )}
                </p>
              )}
              {moving?.id === p.id && (
                <div className="planned-move">
                  <WhenChoice what={p.what} value={moving.when} onChange={(when) => setMoving({ id: p.id, when })} testid="planned-when" />
                  <button className="primary" disabled={state.busy || moving.when.kind === "now"} onClick={() => void run(() => api(`/api/plan/${p.id}`, { method: "PATCH", body: { at: whenAt(moving.when, p.what) } }))} data-testid="planned-move-save">
                    Verschieben
                  </button>
                </div>
              )}
            </li>
          );
        })}
      </ul>
      {state.error && <p className="error">{state.error}</p>}
    </div>
  );
}
